import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LabelList,
  ReferenceLine,
  ReferenceDot,
} from "recharts";
import { Header } from "../components/Header";
import { api } from "../lib/api";
import { STRATEGIES, STRATEGY_BY_KEY } from "../lib/strategies";

// Distinct colors for up to ~10 symbols
const PALETTE = ["#00E676", "#FF9F0A", "#007AFF", "#FF3B30", "#BF5AF2", "#FFD60A", "#00B8D4", "#F97316", "#22D3EE", "#F472B6"];
// Distinct colors per chain within one symbol panel
const CHAIN_COLORS = ["#FFD60A", "#FF9F0A", "#BF5AF2", "#00B8D4", "#F472B6"];

// Small "✕" marker at a contract's expiration date, with optional date label
// for contracts still running (options convention: ✕ = expiry).
const ExpMarker = ({ cx, cy, color, label }) => (
  <g>
    <line x1={cx - 3.5} y1={cy - 3.5} x2={cx + 3.5} y2={cy + 3.5} stroke={color} strokeWidth={1.5} />
    <line x1={cx - 3.5} y1={cy + 3.5} x2={cx + 3.5} y2={cy - 3.5} stroke={color} strokeWidth={1.5} />
    {label && (
      <text x={cx} y={cy - 8} textAnchor="middle" fill={color} fontSize={9} fontFamily="monospace">
        exp {label}
      </text>
    )}
  </g>
);

// Renders a small "+$X" or "-$X" badge above each chain transition dot.
// Only renders when the underlying data value is a non-null number, so it
// naturally skips rows in between transitions (which are null in the credit
// column) — giving one label per open/roll/close event.
const renderCreditLabel = (color) => (props) => {
  const { x, y, value } = props;
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
  const v = Number(value);
  const sign = v >= 0 ? "+" : "−";
  const abs = Math.abs(v);
  return (
    <text
      x={x}
      y={y - 6}
      textAnchor="middle"
      fill={color}
      fontSize={10}
      fontFamily="monospace"
      fontWeight={600}
    >
      {sign}${abs.toFixed(2)}
    </text>
  );
};

// Custom Recharts Tooltip that surfaces rich chain-transition detail when the
// hovered row lands on a chain point (strike, kind, net credit, underlying at
// event). Falls back to a simple "close price" for non-transition hovers.
const KIND_LABEL = {
  create: "Opened",
  phantom_create: "Opened (est.)",
  roll: "Rolled",
  close: "Closed",
  expired: "Expired",
  still_open: "Still open",
};

const ChainTooltip = ({ active, payload, chains }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload || {};
  const tsLabel = (() => {
    const d = new Date(row.ts_ms);
    if (Number.isNaN(+d)) return "";
    return d.toLocaleDateString([], { month: "short", day: "2-digit", year: "numeric" });
  })();
  const close = row.close;
  const chainMetas = chains
    .map((c) => row[`chain_meta_${c.chain_id}`])
    .filter(Boolean);
  return (
    <div
      style={{
        background: "#0A0A0A",
        border: "1px solid #3F3F46",
        padding: "6px 8px",
        fontSize: 11,
        fontFamily: "monospace",
        color: "#E4E4E7",
        minWidth: 180,
      }}
      data-testid="chain-tooltip"
    >
      <div style={{ color: "#00E676", marginBottom: 4 }}>{tsLabel}</div>
      {typeof close === "number" && (
        <div style={{ color: "#A1A1AA" }}>
          Close: <span style={{ color: "#E4E4E7" }}>${close.toFixed(2)}</span>
        </div>
      )}
      {chainMetas.map((m, i) => (
        <div
          key={i}
          style={{
            marginTop: 6,
            paddingTop: 6,
            borderTop: "1px solid #27272A",
            lineHeight: 1.5,
          }}
        >
          <div>
            <span style={{ color: "#71717A" }}>{KIND_LABEL[m.kind] || m.kind}</span>
            <span style={{ color: "#E4E4E7", marginLeft: 6 }}>@ ${m.strike}</span>
          </div>
          {m.net_credit !== null && m.net_credit !== undefined && (
            <div style={{ color: m.net_credit >= 0 ? "#00E676" : "#FF3B30" }}>
              {m.net_credit >= 0 ? "+" : "−"}${Math.abs(m.net_credit).toFixed(2)} credit
            </div>
          )}
          {m.underlying_at_event !== null && m.underlying_at_event !== undefined && (
            <div style={{ color: "#A1A1AA" }}>
              underlying: <span style={{ color: "#E4E4E7" }}>${m.underlying_at_event.toFixed(2)}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default function ChartPage() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const strategyKey = searchParams.get("strategy") || "all";
  const activeStrategy = strategyKey === "all" ? null : STRATEGY_BY_KEY[strategyKey];

  const load = async (symsCsv) => {
    setLoading(true);
    try {
      const params = {};
      if (symsCsv) params.symbols = symsCsv;
      if (activeStrategy) params.strategy = activeStrategy.key;
      const r = await api.get("/history/chart", { params, withCredentials: true });
      setData(r.data);
      if (selected.length === 0) setSelected(r.data.selected || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load chart data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setSelected([]); load(); /* eslint-disable-next-line */ }, [strategyKey]);

  const toggle = (sym) => {
    const next = selected.includes(sym) ? selected.filter((s) => s !== sym) : [...selected, sym];
    setSelected(next);
    load(next.join(","));
  };

  const colorFor = (sym) => {
    const idx = (data?.available || []).indexOf(sym);
    return PALETTE[idx % PALETTE.length] || "#00E676";
  };

  // Build one dataset PER symbol. Each dataset contains:
  //   - rows: merged timeline (trading-day index `x` + close price + one column
  //     per chain with the strike active at that time)
  //   - chains: chain metadata for this symbol
  //
  // The x-axis is a TRADING-SESSION index (0, 1, 2 … one per daily bar), not
  // continuous time — so weekends/holidays take up no horizontal space, like a
  // real stock chart. Arbitrary event timestamps (rolls, closes) are mapped to
  // a fractional index between the surrounding trading days.
  const perSymbol = useMemo(() => {
    if (!data) return {};
    const DAY_MS = 86400000;
    const out = {};
    for (const sym of selected) {
      const symChains = (data.chains || []).filter((c) => c.symbol === sym);
      const bars = (data.series[sym] || []).slice().sort((a, b) => a.ts_ms - b.ts_ms);
      const lastRealIdx = bars.length - 1;
      // Extend the axis with future TRADING days so upcoming contract
      // expirations are visible ahead of the price line.
      const expMsOf = (iso) => Date.parse(`${iso}T20:00:00Z`);
      let maxExp = 0;
      for (const c of symChains) {
        for (const p of c.points) {
          if (p.expiration) maxExp = Math.max(maxExp, expMsOf(p.expiration));
        }
      }
      if (bars.length && maxExp > bars[bars.length - 1].ts_ms) {
        let t = bars[bars.length - 1].ts_ms;
        let guard = 0;
        while (t < maxExp && guard < 200) {
          t += DAY_MS;
          guard += 1;
          const dow = new Date(t).getUTCDay();
          if (dow === 0 || dow === 6) continue;
          bars.push({ ts_ms: t, date: new Date(t).toISOString(), future: true });
        }
      }
      const times = bars.map((b) => b.ts_ms);
      const n = times.length;
      // Map any timestamp → fractional trading-day index (gap-compressed).
      const xFor = (ts) => {
        if (n === 0) return 0;
        if (ts <= times[0]) return (ts - times[0]) / DAY_MS;
        if (ts >= times[n - 1]) return n - 1 + Math.min(1.5, (ts - times[n - 1]) / DAY_MS);
        let lo = 0, hi = n - 1;
        while (hi - lo > 1) {
          const mid = (lo + hi) >> 1;
          if (times[mid] <= ts) lo = mid;
          else hi = mid;
        }
        return lo + (ts - times[lo]) / (times[hi] - times[lo]);
      };
      const rows = bars.map((p, i) => ({
        x: i,
        ts_ms: p.ts_ms,
        date: p.date,
        close: p.close,
      }));
      const byMs = new Map(rows.map((r) => [r.ts_ms, r]));

      // For each chain: ensure anchor rows exist at every transition point AND
      // populate the chain's column with the strike active at each ts_ms so a
      // stepAfter Line correctly steps at transition boundaries. Also seed a
      // `chain_credit_<id>` column with the credit value ONLY at exact
      // transition timestamps — used by the LabelList to render a small
      // "+$X" badge above each step transition dot.
      for (const chain of symChains) {
        const key = `chain_${chain.chain_id}`;
        const creditKey = `chain_credit_${chain.chain_id}`;
        const metaKey = `chain_meta_${chain.chain_id}`;
        // Anchor rows at every transition point
        for (const p of chain.points) {
          if (!byMs.has(p.ts_ms)) {
            byMs.set(p.ts_ms, { ts_ms: p.ts_ms, x: xFor(p.ts_ms), date: new Date(p.ts_ms).toISOString() });
          }
        }
        // Stamp credit + meta on the anchor row for each transition point
        for (const p of chain.points) {
          const row = byMs.get(p.ts_ms);
          if (row) {
            row[creditKey] = p.net_credit;
            row[metaKey] = {
              strike: p.strike,
              kind: p.kind,
              net_credit: p.net_credit,
              underlying_at_event: p.underlying_at_event,
              ts: p.ts,
            };
          }
        }
        // Build a piecewise-constant strike function from the points and
        // apply it to every row within the chain's window.
        const pts = chain.points; // already sorted by ts
        for (const [ms, row] of byMs) {
          if (ms < pts[0].ts_ms || ms > pts[pts.length - 1].ts_ms) continue;
          // Find the strike active at ms: the latest point with ts_ms <= ms.
          let strike = pts[0].strike;
          for (let i = 0; i < pts.length; i++) {
            if (pts[i].ts_ms <= ms) strike = pts[i].strike;
            else break;
          }
          row[key] = strike;
        }
      }

      const merged = [...byMs.values()].sort((a, b) => a.x - b.x);
      // Contract lifespan spans: one per sold contract (create/roll point with
      // an expiration) — dotted line from trade point to expiration date.
      const spans = [];
      symChains.forEach((chain, idx) => {
        for (let i = 0; i < chain.points.length; i++) {
          const p = chain.points[i];
          if (!p.expiration) continue;
          if (!["create", "phantom_create", "roll"].includes(p.kind)) continue;
          const expMs = expMsOf(p.expiration);
          const x2 = xFor(expMs);
          const d = new Date(expMs);
          spans.push({
            key: `${chain.chain_id}-${i}`,
            chainIdx: idx,
            x1: xFor(p.ts_ms),
            x2,
            strike: p.strike,
            expLabel: x2 > lastRealIdx ? d.toLocaleDateString([], { month: "short", day: "2-digit" }) : null,
          });
        }
      });
      // Evenly spaced integer ticks (~7 labels) mapped back to bar dates.
      const step = Math.max(1, Math.round(n / 7));
      const ticks = [];
      for (let i = 0; i < n; i += step) ticks.push(i);
      if (n > 0 && ticks[ticks.length - 1] !== n - 1) ticks.push(n - 1);
      const tickLabel = (v) => {
        const i = Math.min(n - 1, Math.max(0, Math.round(v)));
        const b = bars[i];
        if (!b) return "";
        const d = new Date(b.ts_ms);
        return d.toLocaleDateString([], { month: "short", day: "2-digit" });
      };
      out[sym] = { rows: merged, chains: symChains, ticks, tickLabel, spans };
    }
    return out;
  }, [data, selected]);

  const totalChains = useMemo(
    () => (data?.chains || []).filter((c) => selected.includes(c.symbol)).length,
    [data, selected]
  );

  return (
    <div className="App flex flex-col h-screen">
      <Header />
      <main className="flex-1 overflow-y-auto max-w-[1600px] mx-auto w-full px-6 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-cap">Timeline</div>
            <h1 className="text-2xl font-bold tracking-tight mt-1 flex items-center gap-2">
              <TrendingUp size={20} className="text-[#00E676]" /> Position Timeline
            </h1>
            <p className="text-xs text-[#A1A1AA] mt-1 leading-relaxed max-w-lg">
              Underlying price over time with each covered-call position overlaid as a horizontal
              line at its strike, spanning from open to close/roll. Dotted tails show each
              contract's lifespan to its expiration date (✕). Historical closes via Tradier.
            </p>
          </div>
          <Link to="/" className="btn-outline flex items-center gap-1.5" data-testid="chart-back-btn">
            <ArrowLeft size={14} /> Dashboard
          </Link>
        </div>

        {/* Strategy filter */}
        <div className="flex items-center gap-1 flex-wrap" data-testid="chart-strategy-filter">
          <button
            onClick={() => setSearchParams({})}
            className={`px-3 py-1 border text-[10px] font-bold uppercase tracking-widest transition-colors ${
              strategyKey === "all"
                ? "border-white bg-white text-black"
                : "border-[#27272A] text-[#A1A1AA] hover:border-white"
            }`}
          >
            All
          </button>
          {STRATEGIES.map((s) => (
            <button
              key={s.key}
              onClick={() => setSearchParams({ strategy: s.key })}
              data-testid={`chart-strategy-${s.key}`}
              className={`px-3 py-1 border text-[10px] font-bold uppercase tracking-widest transition-colors ${
                strategyKey === s.key ? "text-black" : "border-[#27272A] text-[#A1A1AA]"
              }`}
              style={
                strategyKey === s.key
                  ? { background: s.accent, borderColor: s.accent }
                  : { color: s.accent, borderColor: "#27272A" }
              }
            >
              {s.short} · {s.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="border border-[#FF3B30] bg-[rgba(255,59,48,0.06)] p-3 text-sm text-[#FFD9D6]">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4">
          <aside className="panel p-4 space-y-3 h-fit" data-testid="chart-symbol-select">
            <div className="label-cap">Symbols</div>
            {(data?.available || []).length === 0 && !loading && (
              <p className="text-[11px] text-[#71717A] leading-relaxed">
                No positions with history yet — add or roll a position on the Dashboard to see it
                plotted here.
              </p>
            )}
            <div className="space-y-1.5">
              {(data?.available || []).map((sym) => (
                <label
                  key={sym}
                  data-testid={`chart-symbol-${sym}`}
                  className="flex items-center gap-2 cursor-pointer hover:bg-[#121212] px-2 py-1 -mx-2 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(sym)}
                    onChange={() => toggle(sym)}
                    className="accent-[#00E676]"
                  />
                  <span
                    className="w-3 h-0.5 flex-shrink-0"
                    style={{ backgroundColor: colorFor(sym) }}
                  />
                  <span className="font-mono text-xs text-white">{sym}</span>
                </label>
              ))}
            </div>
            {totalChains > 0 && (
              <div className="pt-3 border-t border-[#27272A] text-[10px] font-mono text-[#71717A] leading-relaxed">
                {totalChains} chain{totalChains !== 1 ? "s" : ""} plotted
              </div>
            )}
          </aside>

          <div className="space-y-4" data-testid="chart-canvas">
            {loading && (
              <div className="panel p-4 text-center text-[#71717A] py-24 text-xs">
                Loading historical data…
              </div>
            )}
            {!loading && selected.length === 0 && (
              <div className="panel p-4 text-center text-[#71717A] py-24 text-xs">
                Select at least one symbol on the left.
              </div>
            )}
            {!loading &&
              selected.length > 0 &&
              selected.map((sym) => {
                const sd = perSymbol[sym];
                if (!sd || sd.rows.length === 0) {
                  return (
                    <div key={sym} className="panel p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-3 h-0.5" style={{ backgroundColor: colorFor(sym) }} />
                        <span className="font-mono text-xs text-white">{sym}</span>
                      </div>
                      <div className="text-center text-[#71717A] py-12 text-xs">
                        No historical data available for {sym}.
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={sym} className="panel p-4" data-testid={`chart-panel-${sym}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="w-3 h-0.5" style={{ backgroundColor: colorFor(sym) }} />
                        <span className="font-mono text-xs text-white">{sym}</span>
                        <span className="text-[10px] font-mono text-[#71717A]">
                          · {sd.chains.length} chain{sd.chains.length !== 1 ? "s" : ""}
                        </span>
                        {(() => {
                          const total = sd.chains.reduce((acc, c) => acc + (c.total_credit || 0), 0);
                          if (total <= 0) return null;
                          return (
                            <span
                              className="text-[10px] font-mono text-[#00E676] ml-2"
                              data-testid={`chart-total-credit-${sym}`}
                            >
                              · total ${total.toFixed(2)} collected
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={sd.rows} margin={{ top: 24, right: 40, left: 8, bottom: 8 }}>
                        <CartesianGrid stroke="#27272A" strokeDasharray="2 2" vertical={false} />
                        <XAxis
                          dataKey="x"
                          type="number"
                          domain={["dataMin", "dataMax"]}
                          ticks={sd.ticks}
                          tick={{ fill: "#71717A", fontSize: 10, fontFamily: "monospace" }}
                          axisLine={{ stroke: "#3F3F46" }}
                          tickLine={false}
                          minTickGap={60}
                          tickFormatter={sd.tickLabel}
                        />
                        <YAxis
                          tick={{ fill: "#71717A", fontSize: 10, fontFamily: "monospace" }}
                          axisLine={{ stroke: "#3F3F46" }}
                          tickLine={false}
                          domain={["auto", "auto"]}
                          tickFormatter={(v) => `$${v.toFixed(v < 10 ? 2 : 0)}`}
                        />
                        <Tooltip
                          content={<ChainTooltip chains={sd.chains} />}
                          cursor={{ stroke: "#3F3F46", strokeDasharray: "3 3" }}
                        />
                        <Line
                          type="linear"
                          dataKey="close"
                          stroke={colorFor(sym)}
                          strokeWidth={2}
                          dot={false}
                          isAnimationActive={false}
                          connectNulls
                          name={sym}
                        />
                        {(sd.spans || []).map((s) => {
                          const color = CHAIN_COLORS[s.chainIdx % CHAIN_COLORS.length];
                          return (
                            <React.Fragment key={s.key}>
                              <ReferenceLine
                                segment={[{ x: s.x1, y: s.strike }, { x: s.x2, y: s.strike }]}
                                stroke={color}
                                strokeDasharray="2 5"
                                strokeOpacity={0.35}
                                ifOverflow="extendDomain"
                              />
                              <ReferenceDot
                                x={s.x2}
                                y={s.strike}
                                r={4}
                                ifOverflow="extendDomain"
                                shape={(props) => <ExpMarker cx={props.cx} cy={props.cy} color={color} label={s.expLabel} />}
                              />
                            </React.Fragment>
                          );
                        })}
                        {sd.chains.map((chain, idx) => {
                          // Distinct color per chain within the same symbol so
                          // multiple concurrent chains (from bad data entry or
                          // parallel positions) don't get visually confused.
                          const chainColor = CHAIN_COLORS[idx % CHAIN_COLORS.length];
                          const strikes = chain.points.map((p) => p.strike);
                          const strikeSummary = [...new Set(strikes)].map((s) => `$${s}`).join(" → ");
                          const metaKey = `chain_meta_${chain.chain_id}`;
                          // Draw a dot ONLY on actual trade events (rows carrying meta),
                          // so the strike line stays a clean readable stroke elsewhere.
                          const tradeDot = (props) => {
                            const { cx, cy, payload } = props;
                            if (cx == null || cy == null || !payload || !payload[metaKey]) return null;
                            return (
                              <circle
                                key={`${chain.chain_id}-${payload.ts_ms}`}
                                cx={cx}
                                cy={cy}
                                r={4}
                                fill={chainColor}
                                stroke="#0A0A0A"
                                strokeWidth={1.5}
                              />
                            );
                          };
                          return (
                            <Line
                              key={chain.chain_id}
                              type="stepAfter"
                              dataKey={`chain_${chain.chain_id}`}
                              stroke={chainColor}
                              strokeWidth={1.5}
                              strokeDasharray={chain.phantom ? "3 3" : "5 3"}
                              strokeOpacity={0.95}
                              dot={tradeDot}
                              activeDot={{ r: 5, fill: chainColor, stroke: "#0A0A0A", strokeWidth: 2 }}
                              isAnimationActive={false}
                              connectNulls
                              legendType="none"
                              name={strikeSummary}
                            >
                              <LabelList
                                dataKey={`chain_credit_${chain.chain_id}`}
                                position="top"
                                content={renderCreditLabel(chainColor)}
                              />
                            </Line>
                          );
                        })}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                );
              })}
          </div>
        </div>
      </main>
    </div>
  );
}

